<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input
          v-model="filters.examName"
          placeholder="测评名称"
          clearable
          class="filter-input"
          @keyup.enter="fetchList"
        />
        <el-input
          v-model="filters.examId"
          placeholder="测评 ID"
          clearable
          class="filter-input narrow"
          @keyup.enter="fetchList"
        />
        <el-button type="primary" @click="fetchList">查询</el-button>
      </div>
    </div>

    <p class="page-hint">
      配置各测评的金/银/铜徽章发放规则。未单独保存过的测评显示默认规则，保存后即写入数据库。
    </p>

    <el-table :data="items" v-loading="loading" style="width: 100%">
      <el-table-column prop="examId" label="ID" width="72" />
      <el-table-column prop="examName" label="测评名称" min-width="160" show-overflow-tooltip />
      <el-table-column label="发放模式" width="110">
        <template #default="s">{{ s.row.awardModeLabel }}</template>
      </el-table-column>
      <el-table-column label="难度档" width="88">
        <template #default="s">{{ difficultyLabel(s.row.examDifficulty) }}</template>
      </el-table-column>
      <el-table-column label="分数线 %" width="130">
        <template #default="s">
          金{{ s.row.goldMinPercent }} / 银{{ s.row.silverMinPercent }} / 铜{{ s.row.bronzeMinPercent }}
        </template>
      </el-table-column>
      <el-table-column label="启用" width="72">
        <template #default="s">
          <el-tag :type="s.row.enabled ? 'success' : 'info'" size="small">
            {{ s.row.enabled ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="已配置" width="80">
        <template #default="s">
          <el-tag :type="s.row.hasConfig ? 'success' : 'warning'" size="small">
            {{ s.row.hasConfig ? '是' : '默认' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="ruleSummary" label="规则说明" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="s">
          <el-button size="small" type="primary" @click="openEdit(s.row)">配置</el-button>
        </template>
      </el-table-column>
    </el-table>

    <AdminPagination
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :total="total"
      @size-change="fetchList"
      @current-change="fetchList"
    />

    <el-dialog v-model="dialogVisible" title="测评徽章配置" width="520px" destroy-on-close>
      <p v-if="editRow" class="dialog-exam-name">{{ editRow.examName }}（ID: {{ editRow.examId }}）</p>
      <el-form v-if="editForm" label-width="108px">
        <el-form-item label="发放模式">
          <el-select v-model="editForm.awardMode" style="width: 100%">
            <el-option label="按总得分" value="score" />
            <el-option label="按完成度" value="completion" />
            <el-option label="按维度突出" value="dimension" />
          </el-select>
        </el-form-item>
        <el-form-item label="难度档">
          <el-select v-model="editForm.examDifficulty" style="width: 100%">
            <el-option label="入门 easy" value="easy" />
            <el-option label="常规 normal" value="normal" />
            <el-option label="挑战 hard" value="hard" />
          </el-select>
          <p class="field-hint">完成模式下用于说明文案，如「挑战卷做完即银」</p>
        </el-form-item>
        <el-form-item label="金线 %">
          <el-input-number v-model="editForm.goldMinPercent" :min="1" :max="100" />
        </el-form-item>
        <el-form-item label="银线 %">
          <el-input-number v-model="editForm.silverMinPercent" :min="1" :max="100" />
        </el-form-item>
        <el-form-item label="铜线 %">
          <el-input-number v-model="editForm.bronzeMinPercent" :min="1" :max="100" />
        </el-form-item>
        <el-form-item label="启用徽章">
          <el-switch v-model="editForm.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-alert v-if="rulePreview" :title="rulePreview" type="info" :closable="false" show-icon />
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveConfig">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import { AWARD_MODE_RULES } from '@/utils/examBadge'

export default {
  name: 'AdminExamBadgeManagement',
  components: { AdminPagination },
  data() {
    return {
      loading: false,
      saving: false,
      items: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: { examName: '', examId: '' },
      dialogVisible: false,
      editRow: null,
      editForm: null
    }
  },
  computed: {
    rulePreview() {
      if (!this.editForm) return ''
      const mode = AWARD_MODE_RULES[this.editForm.awardMode]
      if (!mode) return ''
      const g = this.editForm.goldMinPercent
      const s = this.editForm.silverMinPercent
      const b = this.editForm.bronzeMinPercent
      return mode.template(g, s, b)
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    difficultyLabel(d) {
      const map = { easy: '入门', normal: '常规', hard: '挑战' }
      return map[d] || d || '—'
    },
    async fetchList() {
      this.loading = true
      try {
        const examId = this.filters.examId ? Number(this.filters.examId) : 0
        const res = await axios.post('/api/v1/admin/exam/badge/list', {
          page: this.currentPage,
          pageSize: this.pageSize,
          examId: examId || undefined,
          examName: this.filters.examName || undefined
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.items = res.data.data?.items || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openEdit(row) {
      this.editRow = row
      this.editForm = {
        examId: row.examId,
        awardMode: row.awardMode || 'score',
        examDifficulty: row.examDifficulty || 'normal',
        goldMinPercent: row.goldMinPercent || 90,
        silverMinPercent: row.silverMinPercent || 70,
        bronzeMinPercent: row.bronzeMinPercent || 50,
        enabled: row.enabled ? 1 : 0
      }
      this.dialogVisible = true
    },
    async saveConfig() {
      if (!this.editForm) return
      const { goldMinPercent, silverMinPercent, bronzeMinPercent } = this.editForm
      if (goldMinPercent < silverMinPercent || silverMinPercent < bronzeMinPercent) {
        this.$message.warning('分数线需满足：金 ≥ 银 ≥ 铜')
        return
      }
      this.saving = true
      try {
        const res = await axios.post('/api/v1/admin/exam/badge/update', this.editForm)
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '保存失败')
          return
        }
        this.$message.success('已保存')
        this.dialogVisible = false
        await this.fetchList()
      } catch (e) {
        this.$message.error(e.response?.data?.message || '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.page-hint {
  color: #606266;
  font-size: 14px;
  margin: 0 0 16px;
}
.filter-input { width: 200px; }
.filter-input.narrow { width: 120px; }
.dialog-exam-name {
  margin: 0 0 16px;
  font-weight: 600;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
}
</style>
